import React, { useState } from "react";
import { motion } from "framer-motion";
import LeadershipSection from "./LeadershipSection";
import DemoModal from "../utility/DemoModal";

const AboutUs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
  };

  const handleButtonClick = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <div className="bg-gray-50 text-gray-900 overflow-hidden">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50  pt-12  text-center flex">
        <motion.div
          className="container mx-auto px-5 max-w-5xl"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          variants={fadeInUp}
        >
         <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 bg-gradient-to-r from-[#108dc7] to-[#ef8e38] bg-clip-text text-transparent leading-tight tracking-tight">
  Shaping the Future with Intelligent Innovation
</h1>

          <p className="text-xl text-gray-600 mb-8 mt-4">
            At AiSPRY, we don't just build AI solutions — we engineer
            transformation.
          </p>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-10">
        <div className="container mx-auto px-5 max-w-5xl">
          <div className="flex flex-col md:flex-row gap-8 mb-8">
            {["Our Mission", "Our Vision"].map((title, idx) => (
              <motion.div
                key={title}
                className="bg-white p-8 rounded-2xl shadow-lg flex-1 border-t-4 border-[#108dc7]"
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2, duration: 0.5 }}
                variants={fadeInUp}
              >
                <h3 className="text-3xl text-[#108dc7] mb-5 font-bold">
                  {title}
                </h3>
                <p className="text-gray-600 text-lg">
                  {title === "Our Mission"
                    ? "To provide cutting-edge AI solutions that empower organizations to unlock their full potential, drive innovation, and create lasting value for their stakeholders and society at large."
                    : "To be the global leader in responsible AI development and implementation, making advanced technology accessible, ethical, and beneficial for businesses of all sizes across industries."}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="space-y-12">
            {/* Header with Animation */}

            {/* Animated Content Sections */}
            <div className="space-y-8">
              {/* Founding Section */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#108dc7]"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  Our Foundation
                </h3>
                <p className="text-gray-600 text-lg">
                  Founded in 2017, AiSPRY stands as a beacon of innovation where
                  deep industry expertise meets the power of artificial
                  intelligence. Our goal is simple yet ambitious: to empower
                  organizations by seamlessly embedding AI into the heart of
                  their operations — driving efficiency, precision, and growth.
                </p>
              </motion.div>

              {/* Industries Section */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#ef8e38]"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  Our Core Belief
                </h3>
                <p className="text-gray-600 italic mb-3">
                  "Practical AI, Powerful Results"
                </p>
                <p className="text-gray-600">
                  We believe AI is not just about algorithms — it's about
                  outcomes. Our approach combines data, domain expertise, and
                  intelligent automation to deliver measurable business value.
                </p>
              </motion.div>

              {/* Core Belief Section */}

              {/* Solutions Section */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                  What We Do Best
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    "Predictive & Prescriptive Analytics to drive decision-making",
                    "Computer Vision & Video Intelligence for safety and inspection",
                    "AI-Powered ERP & Dashboard Integrations",
                    "AI in Education through our proprietary AiTutor platform",
                    "End-to-End Digital Transformation",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-start p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="bg-[#108dc7] text-white p-2 rounded-full mr-3">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* AiTutor Highlight */}
              {/* <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#108dc7] to-[#ef8e38] p-6 text-white"
              >
                <h3 className="text-xl font-semibold mb-3">
                  AiTutor: Transforming Education with AI
                </h3>
                <p className="mb-4">
                  Our flagship platform revolutionizes learning by delivering
                  personalized, adaptive experiences. From upskilling students
                  to future-proofing professionals, AiTutor bridges knowledge
                  gaps and creates a future-ready workforce.
                </p>
              </motion.div> */}

              {/* Ecosystem Section */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-center bg-white p-6 rounded-xl shadow-sm"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  Our Ecosystem of Growth
                </h3>
                <p className="text-gray-600">
                  We thrive on partnerships and shared progress. In
                  collaboration with 360DigiTMG and global allies, we nurture AI
                  talent, lead skilling initiatives, and co-create solutions
                  that benefit industry and society alike.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="pb-8 bg-white">
        <div className="container mx-auto px-5 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#108dc7] to-[#ef8e38] bg-clip-text text-transparent">
              Our Values
            </h2>
            <p className="text-gray-600">
              These core principles guide everything we do at AiSPRY.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              "Innovation",
              "Integrity",
              "Excellence",
              "Collaboration",
              "Responsibility",
              "Inclusivity",
            ].map((title, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md border-l-4 border-[#108dc7] hover:scale-[1.02] transition-transform"
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                variants={fadeInUp}
              >
                <h3 className="text-2xl font-semibold text-blue-700 mb-2">
                  {title}
                </h3>
                <p className="text-gray-800 text-lg">
                  {title === "Innovation"
                    ? "We constantly push the boundaries of what's possible, exploring new ideas and approaches to deliver breakthrough solutions for our clients."
                    : title === "Integrity"
                    ? "We uphold the highest ethical standards in all our work, ensuring transparency, honesty, and fairness in every interaction.  "
                    : title === "Excellence"
                    ? "We strive for excellence in everything we do, continuously refining our skills and processes to deliver exceptional results."
                    : title === "Collaboration"
                    ? "We believe in the power of partnership, working closely with our clients and each other to achieve shared goals."
                    : title === "Responsibility"
                    ? "We recognize the impact of our work and take responsibility for ensuring our AI solutions are developed and used ethically."
                    : "We embrace diversity in all its forms, fostering an inclusive environment that welcomes different perspectives and experiences."}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <LeadershipSection />

      {/* CTA Section */}
      <section className="pb-20 bg-gradient-to-br from-blue-50 to-indigo-50 text-center">
        <motion.div
          className="container mx-auto px-5 max-w-3xl"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          variants={fadeInUp}
        >
          <h2 className="text-5xl font-bold mb-5">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-gray-600 mb-8">
            Partner with AiSPRY to unlock new possibilities and drive innovation
            in your organization.
          </p>
          <button
            onClick={handleButtonClick}
            className="bg-[#108dc7] text-white px-6 py-3 rounded-md transition-colors"
          >
            Get in Touch
          </button>
        </motion.div>
        {isModalOpen && <DemoModal setIsModalOpen={setIsModalOpen} />}
      </section>
    </div>
  );
};

export default AboutUs;
